import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { BRAND, SERVICES, AUDIENCES, VALUES } from "../lib/brand";
import TrustSection from "../components/TrustSection";
import useSeo from "../lib/useSeo";

const Hero = () => (
  <section
    data-testid="home-hero"
    className="relative overflow-hidden border-b border-bos-ice"
  >
    <div className="absolute inset-0 bos-ground opacity-60" aria-hidden />
    <div className="relative mx-auto max-w-[1240px] px-6 lg:px-10 pt-20 lg:pt-24 pb-24 lg:pb-36">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-16 items-end">
        <div className="bos-stagger">
          <p className="bos-eyebrow" data-testid="hero-eyebrow">
            BuildOnce Studio · Singapore
          </p>
          <h1
            data-testid="hero-headline"
            className="mt-6 text-4xl sm:text-5xl lg:text-[60px] font-medium text-bos-navy leading-[1.1] tracking-tight"
          >
            When the strategy lives in your head, the business has to{" "}
            <span className="text-bos-primary">keep guessing.</span>
          </h1>

          <div className="mt-6 flex items-center gap-4" data-testid="hero-slogan">
            <span className="h-px w-10 bg-bos-mid" aria-hidden />
            <span className="text-[12px] tracking-[0.2em] uppercase text-bos-charcoal/70">
              Grounded in clarity. Built to move.
            </span>
          </div>

          <p
            data-testid="hero-subhead"
            className="mt-8 max-w-xl text-[17px] text-bos-charcoal/85 leading-[1.7]"
          >
            You have the expertise. The thinking is done. What's missing is
            the language to carry it, in a pitch, a partner conversation, a
            team briefing. That's what we build together.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/clarity-kit"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-bos-navy text-bos-white px-7 py-3.5 rounded-full hover:bg-bos-primary transition-colors"
            >
              Start with the Clarity Kit
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/clarity-diagnostic"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-2 border border-bos-navy text-bos-navy px-7 py-3.5 rounded-full hover:bg-bos-navy hover:text-bos-white transition-colors"
            >
              Try the Free Clarity Check
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="lg:pb-8">
          <div
            data-testid="hero-side-card"
            className="bg-bos-ice/80 border border-bos-light/60 p-8 rounded-sm relative"
          >
            <div className="absolute -top-3 left-8 px-3 py-1 bg-bos-navy text-bos-white text-[11px] tracking-[0.16em] uppercase rounded-full">
              The studio promise
            </div>
            <p className="mt-3 text-[15px] text-bos-charcoal/85 leading-[1.75]">
              Every engagement starts with an honest look at whether the
              process fits the problem, rather than the other way around.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="" className="h-9 w-9" />
              <div className="text-[13px]">
                <div className="font-medium text-bos-navy">Jophy Orenza</div>
                <div className="text-bos-charcoal/60">Founder, BuildOnce Studio</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="border-b border-bos-ice" data-testid="home-philosophy">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div>
        <p className="bos-eyebrow">Why clarity comes first.</p>
        <span className="bos-divider mt-6" />
      </div>
      <div>
        <h2 className="text-3xl lg:text-[44px] font-medium text-bos-navy leading-[1.15] tracking-tight">
          Most founder-led businesses don't stall from lack of effort. They
          stall because the strategy still lives in the founder's head, and
          no one has helped them get it out.
        </h2>
        <p className="mt-8 text-[16px] text-bos-charcoal/85 max-w-2xl">
          We sit down with you, listen carefully, and turn that thinking into
          something your team can carry. Positioning that makes sense out
          loud. Offers that don't compete with each other. Language people
          can repeat without losing the meaning.
        </p>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div
              key={v.title}
              data-testid={`value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-bos-white border border-bos-ice p-6 rounded-sm"
            >
              <div className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">
                Value
              </div>
              <h3 className="mt-3 text-[18px] font-medium text-bos-navy">
                {v.title}
              </h3>
              <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7]">
                {v.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const HOMEPAGE_LADDER = [
  { id: "clarity-kit", label: "Start on your own" },
  { id: "diagnostic", label: "Diagnose the gap" },
  { id: "clarity-sprint", label: "Build the foundation" },
  { id: "ai-clarity-workshop", label: "Make an AI decision" },
  { id: "momentum", label: "Continue with support" },
];

const OfferLadderPreview = () => {
  const items = HOMEPAGE_LADDER.map((row) => ({
    label: row.label,
    service: SERVICES.find((s) => s.id === row.id),
  })).filter((x) => x.service);

  return (
    <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="home-offer-ladder">
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="bos-eyebrow">Where to begin</p>
            <h2 className="mt-4 text-3xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.15]">
              Choose the starting point that matches what is unclear now.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="offer-ladder-see-all"
            className="text-bos-primary bos-link-underline"
          >
            See all services
          </Link>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ label, service: s }, i) => (
            <Link
              key={s.id}
              to="/services"
              data-testid={`home-service-card-${s.id}`}
              className={`group block p-7 rounded-sm border transition-all duration-300 hover:-translate-y-0.5 ${
                s.featured
                  ? "bg-bos-navy text-bos-white border-bos-navy"
                  : "bg-bos-white border-bos-ice hover:border-bos-mid/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-[11px] tracking-[0.16em] uppercase ${
                    s.featured ? "text-bos-light" : "text-bos-mid"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} · {label}
                </span>
                <ArrowUpRight
                  size={16}
                  className={`transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                    s.featured ? "text-bos-light" : "text-bos-mid"
                  }`}
                />
              </div>
              <h3
                className={`mt-5 text-[20px] font-medium ${
                  s.featured ? "text-bos-white" : "text-bos-navy"
                }`}
              >
                {s.name}
              </h3>
              <p
                className={`mt-3 text-[14px] leading-[1.7] ${
                  s.featured ? "text-bos-light/90" : "text-bos-charcoal/80"
                }`}
              >
                {s.summary}
              </p>
              <div
                className={`mt-6 text-[12px] tracking-[0.06em] ${
                  s.featured ? "text-bos-light/80" : "text-bos-charcoal/60"
                }`}
              >
                {s.duration}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhoWeServe = () => (
  <section className="border-b border-bos-ice" data-testid="home-audiences">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-16">
        <div>
          <p className="bos-eyebrow">Who we serve</p>
          <h2 className="mt-4 text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
            Founders carrying both business and human complexity.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {AUDIENCES.map((a, i) => (
            <div
              key={a.title}
              data-testid={`audience-${i}`}
              className="border-t border-bos-mid/30 pt-6"
            >
              <div className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-3 text-[20px] font-medium text-bos-navy">
                {a.title}
              </h3>
              <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7]">
                {a.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ClosingCta = () => (
  <section className="bg-bos-navy" data-testid="home-closing-cta">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[2fr_1fr] gap-12 items-end">
      <div>
        <p className="text-[12px] tracking-[0.18em] uppercase text-bos-light">
          Start somewhere honest
        </p>
        <h2 className="mt-6 text-3xl lg:text-[52px] font-medium text-bos-white tracking-tight leading-[1.1]">
          A clear conversation is usually the right first step.
        </h2>
        <p className="mt-6 max-w-xl text-bos-light/90 text-[16px]">
          Book a discovery call and we will spend the first hour listening. No
          deck, no pitch, no pressure. If we are not the right fit, we will say so.
        </p>
      </div>
      <div className="flex flex-col gap-3 lg:items-end">
        <Link
          to="/booking"
          data-testid="closing-cta-book"
          className="inline-flex items-center gap-2 text-bos-light bos-link-underline text-[15px]"
        >
          Book a discovery call
          <ArrowRight size={14} />
        </Link>
        <Link
          to="/clarity-kit"
          data-testid="closing-cta-clarity-kit"
          className="text-bos-light bos-link-underline text-[15px]"
        >
          Or start with the Clarity Kit
        </Link>
      </div>
    </div>
  </section>
);

const ClarityKitFeature = () => (
  <section className="border-b border-bos-ice bg-bos-ice/50" data-testid="home-clarity-kit-feature">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-16 items-start">
      <div>
        <p className="bos-eyebrow">Start here</p>
        <h2 className="mt-4 text-3xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.12]">
          Start with the BuildOnce <span className="text-bos-primary">Clarity Kit.</span>
        </h2>
        <p className="mt-7 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-xl">
          A self-guided decision-making tool for professionals, independent
          experts, and founder-led businesses with valuable experience, too
          many possible directions, and a need to decide what to build, say,
          and sell first.
        </p>

        <div className="mt-8 max-w-xl">
          <p className="text-[14px] text-bos-charcoal/75 leading-[1.7] mb-3">
            The Kit helps you clarify:
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="home-clarity-kit-features">
            {[
              "What you are really offering",
              "Who it is for",
              "Why it matters now",
              "What makes it useful",
              "Where AI may support the work",
              "What the next practical step should be",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[14px] text-bos-charcoal/90 leading-[1.55]">
                <span className="mt-2 h-1 w-1 rounded-full bg-bos-mid shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Link
            to="/clarity-kit"
            data-testid="home-clarity-kit-full"
            className="text-[14px] text-bos-primary bos-link-underline"
          >
            See the full Clarity Kit
          </Link>
          <Link
            to="/clarity-diagnostic"
            data-testid="home-clarity-kit-try"
            className="text-[14px] text-bos-primary bos-link-underline"
          >
            Or try the Free Clarity Check
          </Link>
        </div>
        <p className="mt-4 text-[12px] text-bos-charcoal/65 max-w-md leading-[1.6]">
          A practical first step before a sprint, advisory engagement, or AI build.
        </p>
        <p className="mt-2 text-[12px] text-bos-charcoal/55 max-w-md leading-[1.6]">
          Founding price: SGD 97. Standard price: SGD 149 after the launch period.
        </p>
      </div>

      <div className="bg-bos-white border border-bos-ice rounded-sm p-7 lg:p-8" data-testid="home-clarity-kit-meta">
        <p className="bos-eyebrow">What you get</p>
        <ul className="mt-5 grid grid-cols-1 gap-2.5">
          {[
            "Interactive web app",
            "PDF workbook",
            "7 editable templates",
            "AI reflections after each section",
            "Final clarity summary",
            "Diagnostic credit within 30 days",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-[14px] text-bos-charcoal/90 leading-[1.6]">
              <span className="mt-2 h-1 w-1 rounded-full bg-bos-primary shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const WhenSelfGuidedNotEnough = () => (
  <section className="border-b border-bos-ice" data-testid="home-bridge">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-16 items-start">
        <div>
          <p className="bos-eyebrow">When self-guided clarity is not enough</p>
          <span className="bos-divider mt-6" />
        </div>
        <div>
          <p className="text-[16px] text-bos-charcoal/85 leading-[1.85] max-w-2xl">
            Some people will complete the Kit and know exactly what to do
            next. Others will realise the thinking needs a second pair of
            eyes, especially when positioning, offers, messaging, or AI
            decisions affect the wider business. That is where the Discovery
            Call and Clarity Sprint come in.
          </p>
        </div>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        <Link
          to="/booking"
          data-testid="home-bridge-discovery"
          className="group block bg-bos-white border border-bos-ice p-8 rounded-sm hover:border-bos-mid/60 transition-all duration-300 hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">
              Discovery Call
            </span>
            <ArrowUpRight size={16} className="text-bos-mid group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
          <h3 className="mt-4 text-[22px] font-medium text-bos-navy leading-[1.2]">
            A second pair of eyes on the Kit
          </h3>
          <p className="mt-4 text-[14px] text-bos-charcoal/85 leading-[1.7]">
            For when you have completed the Kit and want help interpreting
            what your answers mean.
          </p>
        </Link>

        <Link
          to="/services"
          data-testid="home-bridge-sprint"
          className="group block bg-bos-navy text-bos-white p-8 rounded-sm hover:bg-bos-primary transition-all duration-300 hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] tracking-[0.16em] uppercase text-bos-light">
              Clarity Sprint
            </span>
            <ArrowUpRight size={16} className="text-bos-light group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
          <h3 className="mt-4 text-[22px] font-medium text-bos-white leading-[1.2]">
            A clearer foundation for the team
          </h3>
          <p className="mt-4 text-[14px] text-bos-light/95 leading-[1.7]">
            For when the business needs clearer positioning, offer structure,
            and shared language the team can actually work from.
          </p>
        </Link>
      </div>
    </div>
  </section>
);

const AIWithoutTheNoise = () => (
  <section className="border-b border-bos-ice" data-testid="home-ai-section">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div>
        <p className="bos-eyebrow">AI, used carefully</p>
        <span className="bos-divider mt-6" />
      </div>
      <div>
        <h2 className="text-3xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.15]">
          AI, after the business question is clear.
        </h2>
        <div className="mt-7 space-y-5 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          <p>
            At BuildOnce, AI is not used to replace judgement. It is used to
            support clearer thinking, better questions, and more grounded
            decisions before money is spent on tools, vendors, or training.
          </p>
          <p>
            The Clarity Kit gives you a first experience of this approach:
            structured reflection, AI-assisted prompts, and a final clarity
            summary you can actually use.
          </p>
        </div>
        <Link
          to="/clarity-diagnostic"
          data-testid="home-ai-section-cta"
          className="mt-8 inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[14px]"
        >
          Try the Free Clarity Check <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  useSeo({
    title: "BuildOnce Studio · Decide what to build, say, and sell first",
    description:
      "Strategy and positioning for founders, consultants and founder-led SMEs deciding what to build, say, or offer next. Singapore and Southeast Asia.",
  });
  return (
    <div data-testid="home-page">
      <Hero />
      <Philosophy />
      <ClarityKitFeature />
      <WhenSelfGuidedNotEnough />
      <AIWithoutTheNoise />
      <OfferLadderPreview />
      <WhoWeServe />
      <TrustSection variant="home" />
      <ClosingCta />
    </div>
  );
}
